import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Link as RouterLink, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchActivityDetails } from './activityApi';
import { activityInteractionTarget } from './activityInteractionPath';
import {
  ACTIVITY_KIND_LABELS,
  ACTIVITY_PERIOD_LABELS,
  ACTIVITY_PERIODS,
  ActivityPeriod,
  isActivityKind,
  isActivityPeriod,
} from './activityTypes';
import {
  statsBackButtonSx,
  statsDetailSummarySx,
  statsInteractionPrimarySx,
  statsInteractionRowSx,
  statsInteractionSecondarySx,
  statsPaperSx,
  statsShellSx,
  statsTabsSx,
  statsTitleSx,
} from './statsMuiStyles';

const PAGE_SIZE = 50;

const formatTimestamp = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const ActivityDetailPage = () => {
  const { kind: kindParam } = useParams<{ kind: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodParam = searchParams.get('period');

  const kind = kindParam && isActivityKind(kindParam) ? kindParam : null;
  const period: ActivityPeriod | 'all' =
    periodParam && isActivityPeriod(periodParam) ? periodParam : 'all';

  const [interactions, setInteractions] = useState<
    Awaited<ReturnType<typeof fetchActivityDetails>>['interactions']
  >([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const loadPage = useCallback(
    async (nextOffset: number, append: boolean) => {
      if (!kind) {
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setErrorMessage('');

      try {
        const data = await fetchActivityDetails(
          kind,
          period,
          nextOffset,
          PAGE_SIZE,
        );
        setTotal(data.total);
        setHasMore(data.hasMore);
        setOffset(nextOffset + data.interactions.length);
        setInteractions((current) =>
          append ? [...current, ...data.interactions] : data.interactions,
        );
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to load activity';
        setErrorMessage(message);
        if (!append) {
          setInteractions([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [kind, period],
  );

  useEffect(() => {
    void loadPage(0, false);
  }, [loadPage]);

  const periodTabs = useMemo(
    () => [...ACTIVITY_PERIODS.map((value) => value as ActivityPeriod | 'all'), 'all' as const],
    [],
  );

  if (!kind) {
    return <Navigate to="/stats" replace />;
  }

  return (
    <Box sx={statsShellSx}>
      <Button component={RouterLink} to="/stats" size="small" sx={statsBackButtonSx}>
        Back to stats
      </Button>

      <Typography component="h1" sx={statsTitleSx}>
        {ACTIVITY_KIND_LABELS[kind]}
      </Typography>

      <Tabs
        value={period}
        onChange={(_, value: ActivityPeriod | 'all') => {
          setSearchParams(value === 'all' ? {} : { period: value });
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={statsTabsSx}
      >
        {periodTabs.map((value) => (
          <Tab
            key={value}
            value={value}
            label={ACTIVITY_PERIOD_LABELS[value]}
          />
        ))}
      </Tabs>

      <Paper variant="outlined" sx={statsPaperSx}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && errorMessage && (
          <Alert severity="warning">{errorMessage}</Alert>
        )}

        {!loading && !errorMessage && (
          <>
            <Typography sx={statsDetailSummarySx}>
              {total === 0
                ? 'No interactions in this period.'
                : `${total} interaction${total === 1 ? '' : 's'}`}
            </Typography>

            {interactions.length === 0 ? (
              <Typography sx={statsDetailSummarySx}>Nothing recorded yet.</Typography>
            ) : (
              <List disablePadding>
                {interactions.map((interaction) => {
                  const target = activityInteractionTarget(kind, interaction, period);

                  return (
                    <ListItemButton
                      key={interaction.id}
                      component={target ? RouterLink : 'div'}
                      to={target?.to ?? ''}
                      state={target?.state}
                      disabled={!target}
                      disableGutters
                      sx={{
                        ...statsInteractionRowSx,
                        cursor: target ? 'pointer' : 'default',
                      }}
                    >
                      <ListItemText
                        primary={interaction.label}
                        secondary={
                          <>
                            {formatTimestamp(interaction.occurredAt)}
                            {interaction.detail ? ` · ${interaction.detail}` : ''}
                          </>
                        }
                        primaryTypographyProps={{ sx: statsInteractionPrimarySx }}
                        secondaryTypographyProps={{ sx: statsInteractionSecondarySx }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={loadingMore}
                  onClick={() => void loadPage(offset, true)}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ActivityDetailPage;
